import { useState } from 'react';

import useRequest from 'web-shared/hooks/useRequest';
import HttpError from 'web-shared/services/http-service/HttpError';
import { useDidUpdate } from '../../../../../../../submodules/web-shared/hooks/effect';
import { useLanguageBasedSort } from '../../../../../../../submodules/web-shared/hooks/useLanguageBasedSort';
import { GrouppedSelectionActions, MapTool } from '../../../types';
import { getOverlays } from '../../api';
import { Overlay, OverlayResponse } from '../../api/types/Overlay';
import { OverlaySection } from '../../components/OverlaysLayer';

const LOCAL_STORAGE_KEY = 'selected-map-overlays';

const getSelectedOverlays = () => {
  const persistedOverlays = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!persistedOverlays) return [];
  return JSON.parse(persistedOverlays) as number[];
};

export const clearPersistedOverlays = () => {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
};

interface OverlaysMapToolData {
  overlaySections: OverlaySection[];
  selectedOverlays: number[];
  loading: boolean;
  error: HttpError | null;
}

export type OverlaysMapTool = MapTool<OverlaysMapToolData, GrouppedSelectionActions<Overlay> & {
  refresh: (params: undefined) => void;
}>;

export const useOverlaysMapTool = (): OverlaysMapTool => {
  const [selectedOverlays, setSelectedOverlays] = useState<number[]>(getSelectedOverlays);
  const { sort } = useLanguageBasedSort({ sensitivity: 'accent' });

  const request = useRequest(getOverlays, undefined, {
    responseModifier: (overlays: OverlayResponse): OverlaySection[] => {
      const { global, ...portals } = overlays;
      const sortByName = <T extends { name: string }, >(data: T[]): T[] => sort(data, item => item.name);
      const portalSections = Object.entries(portals).map(([name, overlays]) => {
        return {
          name,
          overlays: sortByName(overlays)
        };
      });
      const sortedPortalSections = sortByName(portalSections);
      const overlaySections: OverlaySection[] = [];
      if (global) overlaySections.push({
        name: 'global',
        overlays: sortByName(global)
      });
      overlaySections.push(...sortedPortalSections);
      return overlaySections;
    }
  });

  useDidUpdate(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(selectedOverlays));
  }, [selectedOverlays]);

  const overlaySections = request.data || [];

  const toggleOverlay = (overlay: Overlay) => {
    setSelectedOverlays(currentOverlays => {
      if (currentOverlays.includes(overlay.id)) {
        return currentOverlays.filter(selectedId => selectedId !== overlay.id);
      }
      return [...currentOverlays, overlay.id];
    });
  };

  const selectSection = (overlays: Overlay[]) => {
    setSelectedOverlays(currentOverlays => {
      const sectionIds = overlays.map(overlay => overlay.id);
      return Array.from(new Set([...currentOverlays, ...sectionIds]));
    });
  };

  const unselectSection = (overlays: Overlay[]) => {
    setSelectedOverlays(currentOverlays => {
      return currentOverlays.filter(currentOverlay => !overlays
        .find(overlay => overlay.id === currentOverlay));
    });
  };

  const selectAllSections = () => {
    const allOverlayIds = overlaySections.flatMap(section =>
      section.overlays.map(overlay => overlay.id)
    );

    setSelectedOverlays(Array.from(new Set(allOverlayIds)));
  };

  const hideAllSections = () => setSelectedOverlays([]);

  return {
    active: selectedOverlays.length > 0 && !request.error,
    available: true,
    actions: {
      toggleOption: toggleOverlay,
      selectOptions: selectSection,
      unselectOptions: unselectSection,
      selectAllOptions: selectAllSections,
      unSelectAllOptions: hideAllSections,
      refresh: request.trigger,
    },
    data: {
      overlaySections,
      selectedOverlays,
      loading: request.loading,
      error: request.error,
    },
  };
};